import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Container, Paper, TextField, Button, Typography,
    Box, Grid, Alert, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    MenuItem, Chip, FormControlLabel, Checkbox
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    CloudDownload as CloudDownloadIcon,
    Save as SaveIcon
} from '@mui/icons-material';
import api from '../services/api';

function AndamentoForm() {
    const navigate = useNavigate();
    const { processoId, id } = useParams();
    const [processo, setProcesso] = useState(null);
    const [andamentos, setAndamentos] = useState([]);
    const [tiposAndamento, setTiposAndamento] = useState([]);
    const [loading, setLoading] = useState(false);
    const [consultando, setConsultando] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [openConsultaDialog, setOpenConsultaDialog] = useState(false);
    const [andamentosConsulta, setAndamentosConsulta] = useState([]);
    const [editingItem, setEditingItem] = useState(null);
    const [formData, setFormData] = useState({
        data_andamento: '',
        descricao: '',
        tipo_andamento: 'OUTROS',
        visivel_cliente: true
    });
    const [error, setError] = useState('');

    useEffect(() => {
        loadTiposAndamento();
        if (processoId) {
            loadProcesso();
            loadAndamentos();
        }
    }, [processoId]);

    const loadTiposAndamento = async () => {
        try {
            const response = await api.get('/tipo-andamento-lista?ativo=true');
            const tipos = response.data || [];
            // Ordena por ordem e depois por valor
            tipos.sort((a, b) => {
                if (a.ordem !== b.ordem) return (a.ordem || 0) - (b.ordem || 0);
                return a.valor.localeCompare(b.valor);
            });
            const valores = tipos.map(t => t.valor);
            setTiposAndamento(valores.length > 0 ? valores : ['OUTROS']);
        } catch (error) {
            console.error('Erro ao carregar tipos de andamento:', error);
            // Fallback para lista padrão se a API falhar
            setTiposAndamento(['AUDIÊNCIA', 'DECISÃO', 'SENTENÇA', 'RECURSO', 'PERÍCIA', 'CITAÇÃO', 'INTIMAÇÃO', 'DEFERIMENTO', 'INDEFERIMENTO', 'HOMOLOGAÇÃO', 'ACORDO', 'OUTROS']);
        }
    };

    const loadProcesso = async () => {
        try {
            const response = await api.get(`/processos/${processoId}`);
            setProcesso(response.data);
        } catch (error) {
            console.error('Erro ao carregar processo:', error);
            setError('Erro ao carregar dados do processo');
        }
    };

    const loadAndamentos = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/andamentos?processo_id=${processoId}`);
            setAndamentos(response.data || []);
        } catch (error) {
            console.error('Erro ao carregar andamentos:', error);
            setError('Erro ao carregar andamentos');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (item = null) => {
        if (item) {
            setEditingItem(item);
            // IMPORTANTE: Garante que a data seja no formato YYYY-MM-DD para o input type="date"
            let dataFormatada = '';
            if (item.data_andamento && item.data_andamento !== null) {
                const dataStr = item.data_andamento.toString();
                // Se vier como ISO string (com T), extrai apenas a parte da data
                if (dataStr.includes('T')) {
                    dataFormatada = dataStr.split('T')[0];
                } else {
                    // Tenta extrair YYYY-MM-DD da string
                    const match = dataStr.match(/(\d{4}-\d{2}-\d{2})/);
                    dataFormatada = match ? match[1] : dataStr;
                }
            }
            setFormData({
                data_andamento: dataFormatada,
                descricao: (item.descricao && item.descricao !== null) ? item.descricao : '',
                tipo_andamento: (item.tipo_andamento && item.tipo_andamento !== null) ? item.tipo_andamento : 'OUTROS',
                visivel_cliente: item.visivel_cliente !== undefined ? Boolean(item.visivel_cliente) : true
            });
        } else {
            setEditingItem(null);
            // Usa data local (não UTC) para evitar problema de timezone
            const hoje = new Date();
            const ano = hoje.getFullYear();
            const mes = String(hoje.getMonth() + 1).padStart(2, '0');
            const dia = String(hoje.getDate()).padStart(2, '0');
            setFormData({
                data_andamento: `${ano}-${mes}-${dia}`,
                descricao: '',
                tipo_andamento: 'OUTROS',
                visivel_cliente: true
            });
        }
        setOpenDialog(true);
        setError('');
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingItem(null);
        setFormData({
            data_andamento: '',
            descricao: '',
            tipo_andamento: 'OUTROS',
            visivel_cliente: true
        });
        setError('');
    };

    const handleSave = async () => {
        if (!formData.descricao.trim()) {
            setError('Descrição é obrigatória');
            return;
        }

        try {
            // IMPORTANTE: Garante que a data seja enviada como YYYY-MM-DD (sem timezone)
            // O input type="date" já retorna YYYY-MM-DD, mas garantimos aqui para evitar problemas
            const dadosParaEnvio = { ...formData };
            if (dadosParaEnvio.data_andamento) {
                // Se a data vier como ISO string (com T), extrai apenas a parte da data
                if (typeof dadosParaEnvio.data_andamento === 'string' && dadosParaEnvio.data_andamento.includes('T')) {
                    dadosParaEnvio.data_andamento = dadosParaEnvio.data_andamento.split('T')[0];
                }
                // Garante formato YYYY-MM-DD (remove qualquer coisa após a data)
                const match = dadosParaEnvio.data_andamento.match(/(\d{4}-\d{2}-\d{2})/);
                if (match) {
                    dadosParaEnvio.data_andamento = match[1];
                }
            }

            if (editingItem) {
                await api.put(`/andamentos/${editingItem.id}`, dadosParaEnvio);
            } else {
                await api.post('/andamentos', {
                    ...dadosParaEnvio,
                    processo_id: processoId
                });
            }
            handleCloseDialog();
            loadAndamentos();
        } catch (error) {
            console.error('Erro ao salvar andamento:', error);
            setError(error.response?.data?.error || 'Erro ao salvar andamento');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir este andamento?')) {
            try {
                await api.delete(`/andamentos/${id}`);
                loadAndamentos();
            } catch (error) {
                console.error('Erro ao deletar andamento:', error);
                alert('Erro ao deletar andamento: ' + (error.response?.data?.error || error.message));
            }
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        try {
            // IMPORTANTE: Sempre extrai diretamente da string para evitar problemas de timezone
            // Se a data está no formato YYYY-MM-DD ou ISO string, extrai apenas a parte da data
            if (typeof date === 'string') {
                // Remove timezone e hora, mantém apenas YYYY-MM-DD
                const dataLimpa = date.split('T')[0].split(' ')[0];
                const match = dataLimpa.match(/(\d{4})-(\d{2})-(\d{2})/);
                if (match) {
                    // Retorna DD/MM/YYYY
                    return `${match[3]}/${match[2]}/${match[1]}`;
                }
            }
            // Fallback: tenta usar Date, mas com cuidado para timezone
            const dateObj = new Date(date);
            if (isNaN(dateObj.getTime())) return date;
            // Usa métodos UTC para evitar conversão de timezone
            const dia = String(dateObj.getUTCDate()).padStart(2, '0');
            const mes = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
            const ano = dateObj.getUTCFullYear();
            return `${dia}/${mes}/${ano}`;
        } catch {
            return date;
        }
    };

    const handleConsultarAPI = async () => {
        if (!processo?.numero_processo) {
            alert('Processo não possui número para consulta na API');
            return;
        }

        setConsultando(true);
        setError('');
        try {
            // O backend irá usar a configuração do usuário (certificado, modo_teste, etc.)
            // Passa o processo_id para o backend buscar a Vara cadastrada (ajuda a determinar o tribunal)
            const url = `/consulta-andamentos/${encodeURIComponent(processo.numero_processo)}`;
            const params = new URLSearchParams();
            if (processoId) {
                params.append('processo_id', processoId);
            }
            // Se o modo_teste estiver configurado no backend, não precisa forçar aqui
            // Mas se o número do processo não estiver no formato correto, força modo_teste para evitar erro 400
            // O backend vai usar a configuração do usuário se não passar modo_teste
            const urlComParams = params.toString() ? `${url}?${params.toString()}` : url;
            
            const response = await api.get(urlComParams);
            const dados = response.data;
            
            // IMPORTANTE: Log completo do JSON retornado para debug
            // Isso ajuda a identificar quando a estrutura não é a esperada
            console.log('📦 JSON completo retornado da API:');
            console.log(JSON.stringify(dados, null, 2));
            console.log('📦 Fim do JSON retornado');
            
            if (dados.andamentos && dados.andamentos.length > 0) {
                setAndamentosConsulta(dados.andamentos);
                setOpenConsultaDialog(true);
            } else {
                // Mostra o JSON retornado em um alert para debug
                const jsonRetornado = JSON.stringify(dados, null, 2);
                alert(
                    'Nenhum andamento encontrado na API externa para este processo.\n\n' +
                    '📦 JSON retornado (primeiros 2000 caracteres):\n' +
                    jsonRetornado.substring(0, 2000) +
                    (jsonRetornado.length > 2000 ? '\n\n... (JSON truncado, veja o console para versão completa)' : '') +
                    '\n\n💡 Verifique o console do navegador (F12) para ver o JSON completo.'
                );
            }
        } catch (error) {
            console.error('Erro ao consultar API:', error);
            
            // IMPORTANTE: Log completo do erro e resposta (se houver) para debug
            console.log('📦 Erro completo:');
            console.log('📦 Error object:', error);
            if (error.response) {
                console.log('📦 Response status:', error.response.status);
                console.log('📦 Response headers:', error.response.headers);
                console.log('📦 Response data (JSON completo):');
                console.log(JSON.stringify(error.response.data, null, 2));
                console.log('📦 Fim do JSON de erro');
            } else if (error.request) {
                console.log('📦 Request feito mas sem resposta:', error.request);
            }
            console.log('📦 Fim do log de erro');
            
            const errorMessage = error.response?.data?.error || error.message || 'Erro ao consultar andamentos na API externa';
            setError(errorMessage);
            
            // Mostra o JSON de erro em um alert para debug (se houver)
            let jsonErroParaAlert = '';
            if (error.response?.data) {
                const jsonErro = JSON.stringify(error.response.data, null, 2);
                jsonErroParaAlert = '\n\n📦 JSON de erro retornado (primeiros 2000 caracteres):\n' +
                    jsonErro.substring(0, 2000) +
                    (jsonErro.length > 2000 ? '\n\n... (JSON truncado, veja o console para versão completa)' : '') +
                    '\n\n💡 Verifique o console do navegador (F12) para ver o JSON completo.';
                console.log('📦 JSON de erro para mostrar no alert:');
                console.log(jsonErro);
            }
            
            // Mensagem mais amigável para o usuário
            let mensagemUsuario = errorMessage;
            if (errorMessage.includes('DataJud não configurado') || errorMessage.includes('Tipo de autenticação deve ser "API Key"') || errorMessage.includes('API Key não configurada')) {
                mensagemUsuario = '❌ DataJud (CNJ) não configurado corretamente.\n\n' +
                    '💡 Para usar a API DataJud do CNJ:\n' +
                    '1. Acesse: Configurações → API de Andamentos\n' +
                    '2. Selecione "Tipo de Autenticação": "API Key"\n' +
                    '3. Configure a "URL da API": https://api-publica.datajud.cnj.jus.br\n' +
                    '4. Cole sua "API Key (Chave Pública)" do CNJ\n' +
                    '5. Clique em "Salvar Configuração"\n\n' +
                    '📝 Nota: A API Key deve ser solicitada via contato institucional com o CNJ.\n' +
                    '📝 Nota: O sistema tentará usar PJe público como alternativa quando DataJud não estiver configurado.\n\n' +
                    '⚠️ Erro técnico: ' + errorMessage.substring(0, 200);
            } else if (errorMessage.includes('PJe público') || errorMessage.includes('PJe')) {
                // Se for erro relacionado ao PJe, mostra mensagem mais detalhada
                if (errorMessage.includes('404') || errorMessage.includes('não encontrada')) {
                    mensagemUsuario = '❌ Servidor PJe do tribunal pode estar fora do ar ou com problemas.\n\n' +
                        '💡 O que fazer:\n' +
                        '• Tente novamente mais tarde\n' +
                        '• Verifique se consegue acessar o site do PJe diretamente no navegador\n' +
                        '• Configure a API Key do DataJud (CNJ) para usar fonte alternativa\n\n' +
                        '📝 Detalhes: ' + errorMessage;
                } else if (errorMessage.includes('conectar') || errorMessage.includes('ENOTFOUND') || errorMessage.includes('ECONNREFUSED')) {
                    mensagemUsuario = '❌ Não foi possível conectar ao servidor PJe do tribunal.\n\n' +
                        '⚠️ O servidor pode estar fora do ar ou com problemas de infraestrutura.\n\n' +
                        '💡 O que fazer:\n' +
                        '• Verifique sua conexão com a internet\n' +
                        '• Tente acessar o site do PJe diretamente no navegador\n' +
                        '• Tente novamente mais tarde\n' +
                        '• Configure a API Key do DataJud (CNJ) para usar fonte alternativa\n\n' +
                        '📝 Detalhes: ' + errorMessage;
                } else {
                    mensagemUsuario = '❌ Erro ao consultar PJe público do tribunal.\n\n' +
                        '💡 Possíveis causas:\n' +
                        '• Servidor PJe está fora do ar ou com problemas\n' +
                        '• Problema temporário de infraestrutura\n' +
                        '• URL do sistema mudou\n\n' +
                        '📝 Detalhes: ' + errorMessage;
                }
            }
            
            alert('Erro ao consultar andamentos:\n\n' + mensagemUsuario + jsonErroParaAlert);
        } finally {
            setConsultando(false);
        }
    };

    const handleSalvarAndamentosConsulta = async () => {
        if (andamentosConsulta.length === 0) return;

        if (!window.confirm(`Deseja salvar ${andamentosConsulta.length} andamento(s) encontrado(s) na API externa?`)) {
            return;
        }

        // Valida se tem processo_id
        if (!processoId) {
            alert('Erro: ID do processo não encontrado. Não é possível salvar os andamentos.');
            return;
        }

        setConsultando(true);
        setError('');
        try {
            const numeroProcesso = processo.numero_processo || '';
            if (!numeroProcesso) {
                throw new Error('Número do processo não encontrado');
            }

            // IMPORTANTE: NÃO força modo_teste=true aqui
            // O backend usa a configuração do usuário automaticamente
            // Se o usuário quiser testar, ele deve ativar "Modo Teste" nas configurações
            const response = await api.get(
                `/consulta-andamentos/${encodeURIComponent(numeroProcesso)}?salvar=true&processo_id=${processoId}`
            );
            
            const dados = response.data;
            if (!dados.sucesso) {
                throw new Error(dados.error || 'Erro ao salvar andamentos');
            }

            const inseridos = dados.inseridos || 0;
            const erros = dados.erros || 0;
            const total = dados.total || 0;
            
            let mensagem = `✅ Sucesso!\n\n`;
            mensagem += `📊 Total encontrado: ${total}\n`;
            mensagem += `✅ Salvos: ${inseridos}\n`;
            if (erros > 0) {
                mensagem += `⚠️ Não salvos (duplicados ou erros): ${erros}`;
            }
            
            alert(mensagem);
            setOpenConsultaDialog(false);
            setAndamentosConsulta([]);
            loadAndamentos(); // Recarrega a lista de andamentos
        } catch (error) {
            console.error('Erro ao salvar andamentos:', error);
            let errorMessage = 'Erro ao salvar andamentos';
            
            if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.response?.data?.detalhes) {
                errorMessage = `${error.response.data.error}\n\nDetalhes: ${error.response.data.detalhes}`;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setError(errorMessage);
            alert('❌ Erro ao salvar andamentos:\n\n' + errorMessage);
        } finally {
            setConsultando(false);
        }
    };

    const handleCloseConsultaDialog = () => {
        setOpenConsultaDialog(false);
        setAndamentosConsulta([]);
    };

    if (!processo) {
        return (
            <Container>
                <Paper sx={{ p: 4, mt: 4 }}>
                    <Typography>Carregando...</Typography>
                </Paper>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg">
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(`/processos/editar/${processoId}`)}
                        sx={{ mb: 1 }}
                    >
                        Voltar para Processo
                    </Button>
                    <Typography variant="h5" component="h1">
                        Gerenciar Andamentos
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Processo: {processo.numero_processo || 'Sem número'} - {processo.cliente_nome || ''}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        color="info"
                        startIcon={<CloudDownloadIcon />}
                        onClick={handleConsultarAPI}
                        disabled={consultando || !processo?.numero_processo}
                        title={!processo?.numero_processo ? 'Este processo não possui número para consulta na API' : 'Consultar andamentos na API externa'}
                    >
                        {consultando ? 'Consultando...' : 'Consultar via API'}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Novo Andamento
                    </Button>
                </Box>
            </Box>

            {error && !openDialog && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Data</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Origem</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">Carregando...</TableCell>
                            </TableRow>
                        ) : andamentos.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    Nenhum andamento cadastrado. Clique em "Novo Andamento" para adicionar ou "Consultar via API" para buscar andamentos externos.
                                </TableCell>
                            </TableRow>
                        ) : (
                            andamentos.map((andamento) => (
                                <TableRow key={andamento.id} hover>
                                    <TableCell>{formatDate(andamento.data_andamento)}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={andamento.tipo_andamento || 'OUTROS'} 
                                            size="small" 
                                        />
                                    </TableCell>
                                    <TableCell>{andamento.descricao}</TableCell>
                                    <TableCell>
                                        {andamento.origem === 'API_EXTERNA' ? (
                                            <Chip 
                                                label="API Externa" 
                                                size="small" 
                                                color="info"
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Chip 
                                                label="Manual" 
                                                size="small" 
                                                variant="outlined"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            onClick={() => handleOpenDialog(andamento)}
                                            color="primary"
                                            size="small"
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={() => handleDelete(andamento.id)}
                                            color="error"
                                            size="small"
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingItem ? 'Editar Andamento' : 'Novo Andamento'}
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    <Box sx={{ pt: 2 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Data do Andamento"
                                    type="date"
                                    value={formData.data_andamento || ''}
                                    onChange={(e) => setFormData({ ...formData, data_andamento: e.target.value })}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Tipo de Andamento"
                                    value={formData.tipo_andamento || 'OUTROS'}
                                    onChange={(e) => setFormData({ ...formData, tipo_andamento: e.target.value })}
                                >
                                    {tiposAndamento.map((tipo) => (
                                        <MenuItem key={tipo} value={tipo}>
                                            {tipo}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={4}
                                    label="Descrição"
                                    required
                                    value={formData.descricao || ''}
                                    onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.visivel_cliente}
                                            onChange={(e) => setFormData({ ...formData, visivel_cliente: e.target.checked })}
                                        />
                                    }
                                    label="Visível para o cliente na consulta"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de Resultados da Consulta API */}
            <Dialog 
                open={openConsultaDialog} 
                onClose={handleCloseConsultaDialog} 
                maxWidth="md" 
                fullWidth
            >
                <DialogTitle>
                    Resultados da Consulta API Externa
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {andamentosConsulta.length} andamento(s) encontrado(s)
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Data</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Descrição</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {andamentosConsulta.map((andamento, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{formatDate(andamento.data_andamento)}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={andamento.tipo_andamento || 'OUTROS'} 
                                                size="small" 
                                            />
                                        </TableCell>
                                        <TableCell>{andamento.descricao}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConsultaDialog}>
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleSalvarAndamentosConsulta} 
                        variant="contained" 
                        color="primary"
                        startIcon={<SaveIcon />}
                        disabled={consultando}
                    >
                        {consultando ? 'Salvando...' : `Salvar ${andamentosConsulta.length} Andamento(s)`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

export default AndamentoForm;

